import { useState, useEffect } from 'react'
import { Label } from '@creanizer/ui'
import { Slider } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { useConsultationStore } from '../../../../stores/consultationStore'

export function ConsultationSettings() {
  const consultationStore = useConsultationStore()
  const [autoSave, setAutoSave] = useState(true)
  const [maxSessions, setMaxSessions] = useState(50)
  const [maxMessagesPerSession, setMaxMessagesPerSession] = useState(100)
  const [useGemini3Preview, setUseGemini3Preview] = useState(false)
  const [useGemini3FlashPreview, setUseGemini3FlashPreview] = useState(false)
  const [useGemini3FlashLitePreview, setUseGemini3FlashLitePreview] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  useEffect(() => {
    // 既存の設定を読み込み
    if (consultationStore.settings) {
      setAutoSave(consultationStore.settings.autoSave)
      setMaxSessions(consultationStore.settings.maxSessions)
      setMaxMessagesPerSession(consultationStore.settings.maxMessagesPerSession)
      setUseGemini3Preview(consultationStore.settings.useGemini3Preview ?? false)
      setUseGemini3FlashPreview(consultationStore.settings.useGemini3FlashPreview ?? false)
      setUseGemini3FlashLitePreview(consultationStore.settings.useGemini3FlashLitePreview ?? false)
    }
  }, [consultationStore.settings])

  useEffect(() => {
    // 変更があるかチェック
    if (consultationStore.settings) {
      const changed =
        autoSave !== consultationStore.settings.autoSave ||
        maxSessions !== consultationStore.settings.maxSessions ||
        maxMessagesPerSession !== consultationStore.settings.maxMessagesPerSession ||
        useGemini3Preview !== (consultationStore.settings.useGemini3Preview ?? false) ||
        useGemini3FlashPreview !== (consultationStore.settings.useGemini3FlashPreview ?? false) ||
        useGemini3FlashLitePreview !== (consultationStore.settings.useGemini3FlashLitePreview ?? false)
      setHasChanges(changed)
    }
  }, [autoSave, maxSessions, maxMessagesPerSession, useGemini3Preview, useGemini3FlashPreview, useGemini3FlashLitePreview, consultationStore.settings])

  const handleSave = () => {
    consultationStore.updateSettings({
      autoSave,
      maxSessions,
      maxMessagesPerSession,
      useGemini3Preview,
      useGemini3FlashPreview,
      useGemini3FlashLitePreview
    })
    setHasChanges(false)
  }

  const handleReset = () => {
    setAutoSave(true)
    setMaxSessions(50)
    setMaxMessagesPerSession(100)
    setUseGemini3Preview(false)
    setUseGemini3FlashPreview(false)
    setUseGemini3FlashLitePreview(false)
    consultationStore.updateSettings({
      autoSave: true,
      maxSessions: 50,
      maxMessagesPerSession: 100,
      useGemini3Preview: false,
      useGemini3FlashPreview: false,
      useGemini3FlashLitePreview: false
    })
    setHasChanges(false)
  }
  
  return (
    <div className="space-y-6">
      {/* セッション自動保存 */}
      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="autoSave"
            checked={autoSave}
            onCheckedChange={(checked) => setAutoSave(checked as boolean)}
          />
          <Label htmlFor="autoSave" className="text-sm font-medium cursor-pointer">
            セッション自動保存
          </Label>
        </div>
        <p className="text-xs text-muted-foreground ml-6">
          すべてのセッションを自動的にIndexedDBに保存します
        </p>
      </div>
      
      {/* 最大セッション数 */}
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <Label className="text-sm font-medium">最大セッション数</Label>
          <span className="text-sm font-medium">{maxSessions}</span>
        </div>
        <Slider
          value={[maxSessions]}
          onValueChange={([value]) => setMaxSessions(value)}
          min={10}
          max={100}
          step={5}
        />
        <p className="text-xs text-muted-foreground">
          最大{maxSessions}セッションまで保存されます（古いものから自動削除）
        </p>
      </div>
      
      {/* 最大メッセージ数 */}
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <Label className="text-sm font-medium">最大メッセージ数</Label>
          <span className="text-sm font-medium">{maxMessagesPerSession}</span>
        </div>
        <Slider
          value={[maxMessagesPerSession]}
          onValueChange={([value]) => setMaxMessagesPerSession(value)}
          min={50}
          max={500}
          step={10}
        />
        <p className="text-xs text-muted-foreground">
          1セッションあたり最大{maxMessagesPerSession}メッセージまで保存されます
        </p>
      </div>

      {/* Gemini 3.0 Pro Preview */}
      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="useGemini3Preview"
            checked={useGemini3Preview}
            onCheckedChange={(checked) => {
              setUseGemini3Preview(checked === true)
              if (checked) { setUseGemini3FlashPreview(false); setUseGemini3FlashLitePreview(false) }
            }}
          />
          <Label htmlFor="useGemini3Preview" className="text-sm font-medium cursor-pointer">
            Gemini 3.0 Pro を使用（テスト運用・課金のみ）
          </Label>
        </div>
        <p className="text-xs text-muted-foreground">
          有効にすると gemini-3.1-pro-preview モデルを使用します。
          デフォルトは gemini-2.5-pro です。
        </p>
        {useGemini3Preview && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            注意: Gemini 3.0 Pro はテスト運用中のため、課金アカウントのAPIキーが必要です。
          </p>
        )}
      </div>

      {/* Gemini 3.0 Flash Preview */}
      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="useGemini3FlashPreview"
            checked={useGemini3FlashPreview}
            onCheckedChange={(checked) => {
              setUseGemini3FlashPreview(checked === true)
              if (checked) { setUseGemini3Preview(false); setUseGemini3FlashLitePreview(false) }
            }}
          />
          <Label htmlFor="useGemini3FlashPreview" className="text-sm font-medium cursor-pointer">
            Gemini 3.0 Flash を使用（テスト運用・課金のみ・高速）
          </Label>
        </div>
        <p className="text-xs text-muted-foreground">
          有効にすると gemini-3.5-flash モデルを使用します。
          Proより高速ですが、精度が若干低い場合があります。
        </p>
        {useGemini3FlashPreview && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            注意: Gemini 3.0 Flash はテスト運用中のため、課金アカウントのAPIキーが必要です。
          </p>
        )}
      </div>

      {/* Gemini 3.1 Flash Lite Preview */}
      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="useGemini3FlashLitePreview"
            checked={useGemini3FlashLitePreview}
            onCheckedChange={(checked) => {
              setUseGemini3FlashLitePreview(checked === true)
              if (checked) { setUseGemini3Preview(false); setUseGemini3FlashPreview(false) }
            }}
          />
          <Label htmlFor="useGemini3FlashLitePreview" className="text-sm font-medium cursor-pointer">
            Gemini 3.1 Flash Lite を使用（テスト運用・課金のみ・超軽量）
          </Label>
        </div>
        <p className="text-xs text-muted-foreground">
          有効にすると gemini-3.5-flash モデルを使用します。
          最も高速・低コストですが、精度は低くなります。
        </p>
        {useGemini3FlashLitePreview && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            注意: Gemini 3.1 Flash Lite はテスト運用中のため、課金アカウントのAPIキーが必要です。
          </p>
        )}
      </div>

      {/* 保存/リセットボタン */}
      <div className="flex gap-2 pt-4 border-t">
        <Button
          onClick={handleSave}
          disabled={!hasChanges}
          className="flex-1"
        >
          設定を保存
        </Button>
        <Button
          onClick={handleReset}
          variant="outline"
        >
          デフォルトに戻す
        </Button>
      </div>
      
      {/* 統計情報 */}
      <div className="pt-4 border-t space-y-2">
        <h4 className="text-sm font-medium">現在の使用状況</h4>
        <div className="text-xs text-muted-foreground space-y-1">
          <p>• 保存済みセッション数: {consultationStore.sessions?.length || 0}件</p>
          <p>• アクティブセッション: {consultationStore.getCurrentActiveSession() ? consultationStore.getCurrentActiveSession()!.title : 'なし'}</p>
          <p>• 総メッセージ数: {consultationStore.countTotalMessages()}件</p>
        </div>
      </div>
      
      {/* データ管理 */}
      <div className="pt-4 border-t space-y-2">
        <h4 className="text-sm font-medium">データ管理</h4>
        <div className="space-y-2">
          <Button
            onClick={() => {
              if (confirm('すべてのセッションを削除しますか？この操作は取り消せません。')) {
                consultationStore.clearAllSessions()
              }
            }}
            variant="destructive"
            className="w-full"
          >
            すべてのセッションを削除
          </Button>
        </div>
      </div>
    </div>
  )
}